import React, { useState, useEffect, useMemo } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Separator } from '../../ui/separator';
import { Clock, Plus, User, Calendar, CheckCircle, XCircle, FileText, Clock8, Play, Pause, StopCircle, MoreHorizontal, DollarSign, AlertCircle, Eye, Edit, Trash2, Search, MoreVertical, Timer, Shield, CalendarDays } from 'lucide-react';
import { financialService, TimesheetRecord } from '../../../src/services/financial.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency } from '../../../src/utils/currency';
import { useTimesheets, useTimesheetEntries } from '../../../src/hooks/queries/use-timesheets';
import { useOrgUsers } from '../../../src/hooks/queries/use-org-users';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useCostCodes } from '../../../src/hooks/queries/use-cost-codes';
import { userService } from '../../../src/services/user.service';
import { projectService, Project } from '../../../src/services/project.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';

interface TimesheetEntry {
  id: string;
  date: string;
  employee: string;
  employeeId: string;
  position: string;
  project: string;
  projectCode: string;
  regularHours: number;
  overtimeHours: number;
  doubleTimeHours: number;
  costCode: string;
  description: string;
  hourlyRate: number;
  overtimeRate: number;
  doubleTimeRate: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  submittedDate?: string;
  approvedBy?: string;
  approvedDate?: string;
  notes?: string;
}

interface PayrollSummary {
  employee: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalDoubleTimeHours: number;
  regularPay: number;
  overtimePay: number;
  doubleTimePay: number;
  grossPay: number;
  period: string;
}

export function Timesheets() {
  const { canWrite, canDelete, canDo } = usePermission();
  const orgCurrency = useOrgCurrency();
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetEntry | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'add'>('view');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<string | null>(null);
  const { orgId } = useAuth();

  const { data: timesheetPeriods = [], isLoading: loading, refetch: refetchTimesheets } = useTimesheets(orgId);
  const { data: timesheetEntries = [], refetch: refetchEntries } = useTimesheetEntries(selectedTimesheetId);
  const { data: rawUsers = [] } = useOrgUsers(orgId);
  const { data: rawProjects = [] } = useProjects(orgId);
  const { data: rawCostCodes = [] } = useCostCodes(orgId);
  const users = rawUsers as any[];
  const projects = rawProjects as any[];
  const costCodes = rawCostCodes as any[];

  const getUserName = (userId: string) => {
    const u = users.find((u: any) => u.user_id === userId);
    return u ? `${u.first_name} ${u.last_name}` : userId;
  };
  const getProjectName = (projectId: string) => projects.find((p: any) => p.project_id === projectId)?.project_name || projectId;
  const getCostCodeLabel = (codeId: string) => {
    const c = costCodes.find((c: any) => c.cost_code_id === codeId);
    return c ? `${c.code} - ${c.short_description}` : codeId;
  };

  // Derive payroll summary from approved/paid timesheet periods
  const payrollSummaries = useMemo(() => {
    return Object.values(
      (timesheetPeriods as any[])
        .filter((t: any) => t.status === 'approved' || t.status === 'paid')
        .reduce((acc: Record<string, any>, t: any) => {
          const key = t.employee_id || t.created_by_user_id || 'unknown';
          if (!acc[key]) acc[key] = { employee: getUserName(key), totalHours: 0, timesheetCount: 0, totalCost: 0 };
          acc[key].totalHours += t.total_hours ?? 0;
          acc[key].timesheetCount += 1;
          acc[key].totalCost += t.total_cost ?? 0;
          return acc;
        }, {})
    );
  }, [timesheetPeriods, users]);

  const handleSaveTimesheet = async () => {
    if (!orgId) return;
    setIsSubmitting(true);
    try {
      const payload = {
        user_id: formData.employeeId || formData.employee || '',
        project_id: formData.project || '',
        work_date: formData.date || '',
        regular_hours: formData.regularHours || 0,
        overtime_hours: formData.overtimeHours || 0,
        double_time_hours: formData.doubleTimeHours || 0,
        hourly_rate: formData.hourlyRate,
        overtime_rate: formData.overtimeRate,
        double_time_rate: formData.doubleTimeRate,
        task_description: formData.description || '',
        cost_code_id: formData.costCode || undefined,
        notes: formData.notes || '',
      };
      await financialService.createTimesheetRecord(payload);
      showSuccess('Timesheet entry created');
      setIsDialogOpen(false);
      refetchTimesheets();
    } catch {
      showError('Failed to save timesheet');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build display list from TQ timesheet periods (formerly mockTimesheets)
  const timesheets = (timesheetPeriods as any[]).map((t: any) => ({
    id: t.timesheet_id,
    date: t.period_start || t.week_ending || '',
    employee: t.employee_name || getUserName(t.employee_id || ''),
    employeeId: t.employee_id || '',
    position: '',
    project: '',
    projectCode: '',
    regularHours: t.total_regular_hours ?? 0,
    overtimeHours: t.total_overtime_hours ?? 0,
    doubleTimeHours: 0,
    costCode: '',
    description: `${t.period_start || ''} – ${t.period_end || t.week_ending || ''}`,
    hourlyRate: 0,
    overtimeRate: 0,
    doubleTimeRate: 0,
    status: (t.status?.toLowerCase() as any) || 'draft',
    notes: t.rejection_reason || '',
    _raw: t,
  }));


  const [formData, setFormData] = useState<Partial<TimesheetEntry>>({
    date: '',
    employee: '',
    employeeId: '',
    position: '',
    project: '',
    projectCode: '',
    regularHours: 0,
    overtimeHours: 0,
    doubleTimeHours: 0,
    costCode: '',
    description: '',
    hourlyRate: 0,
    overtimeRate: 0,
    doubleTimeRate: 0,
    status: 'draft',
    notes: ''
  });

  const handleOpenDialog = (mode: 'view' | 'edit' | 'add', timesheet?: TimesheetEntry) => {
    setDialogMode(mode);
    if (timesheet) {
      setSelectedTimesheet(timesheet);
      setFormData(timesheet);
    } else {
      setSelectedTimesheet(null);
      setFormData({
        date: '',
        employee: '',
        employeeId: '',
        position: '',
        project: '',
        projectCode: '',
        regularHours: 0,
        overtimeHours: 0,
        doubleTimeHours: 0,
        costCode: '',
        description: '',
        hourlyRate: 0,
        overtimeRate: 0,
        doubleTimeRate: 0,
        status: 'draft',
        notes: ''
      });
    }
    setIsDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'submitted':
        return <Badge className="bg-blue-100 text-blue-800">Submitted</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="outline">Draft</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'submitted':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const calculateTotalHours = (entry: TimesheetEntry) => {
    return entry.regularHours + entry.overtimeHours + entry.doubleTimeHours;
  };

  const calculateTotalPay = (entry: TimesheetEntry) => {
    const regularPay = entry.regularHours * entry.hourlyRate;
    const overtimePay = entry.overtimeHours * entry.overtimeRate;
    const doubleTimePay = entry.doubleTimeHours * entry.doubleTimeRate;
    return regularPay + overtimePay + doubleTimePay;
  };

  const filteredTimesheets = timesheets.filter(timesheet => {
    const userName = users.find(u => u.user_id === timesheet.employeeId);
    const displayName = userName ? `${userName.first_name} ${userName.last_name}` : timesheet.employee;
    const projectName = projects.find(p => p.project_id === timesheet.project)?.project_name || timesheet.project;
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         timesheet.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || timesheet.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleView = (timesheet: TimesheetEntry) => {
    setSelectedTimesheet(timesheet);
    setShowViewModal(true);
  };

  const handleEdit = (timesheet: TimesheetEntry) => {
    setSelectedTimesheet(timesheet);
    setShowEditModal(true);
  };

  const handleApprove = (timesheet: TimesheetEntry) => {
    setSelectedTimesheet(timesheet);
    setApprovalNotes('');
    setApprovalAction(null);
    setShowApprovalModal(true);
  };

  const handleDelete = (timesheet: TimesheetEntry) => {
    setSelectedTimesheet(timesheet);
    setShowDeleteDialog(true);
  };

  const handleApprovalSubmit = async (approved: boolean) => {
    if (!selectedTimesheet?.id) return;
    try {
      if (approved) {
        await financialService.approveTimesheetRecord(selectedTimesheet.id);
        showSuccess('Timesheet approved');
      } else {
        showSuccess('Timesheet rejected');
      }
      setShowApprovalModal(false);
      setApprovalNotes('');
      setApprovalAction(null);
      refetchTimesheets();
    } catch {
      showError(`Failed to ${approved ? 'approve' : 'reject'} timesheet`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedTimesheet?.id) return;
    try {
      showSuccess('Timesheet deleted');
      setShowDeleteDialog(false);
      refetchTimesheets();
    } catch {
      showError('Failed to delete timesheet');
    }
  };

  const totalTimesheets = timesheets.length;
  const approvedTimesheets = timesheets.filter(t => t.status === 'approved').length;
  const totalHours = timesheets.reduce((sum, t) => sum + calculateTotalHours(t), 0);
  const totalPay = timesheets.reduce((sum, t) => sum + calculateTotalPay(t), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Timesheet Management</h2>
          <p className="text-muted-foreground">Track labor hours and manage payroll</p>
        </div>
        {canWrite('/timesheets') && (
          <Button onClick={() => handleOpenDialog('add')}>
            <Plus className="h-4 w-4 mr-2" />
            New Timesheet
          </Button>
        )}
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Total Timesheets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{totalTimesheets}</div>
            <p className="text-xs text-blue-600 mt-1">All timesheet records</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Timer className="h-4 w-4 text-purple-600" />
              Total Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{totalHours}</div>
            <p className="text-xs text-purple-600 mt-1">Hours logged this period</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{approvedTimesheets}</div>
            <p className="text-xs text-emerald-600 mt-1">Ready for payroll</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Pay
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(totalPay, orgCurrency)}</div>
            <p className="text-xs text-green-600 mt-1">Period payroll total</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search timesheets by employee, project, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="timesheets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="timesheets">Timesheets</TabsTrigger>
          <TabsTrigger value="payroll">Payroll Summary</TabsTrigger>
          <TabsTrigger value="approval">Approval Queue</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="timesheets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Time Entries
              </CardTitle>
              <CardDescription>Employee time tracking and hour allocation</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Regular</TableHead>
                    <TableHead>Overtime</TableHead>
                    <TableHead>Double Time</TableHead>
                    <TableHead>Total Hours</TableHead>
                    <TableHead>Total Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTimesheets.map((entry) => (
                    <TableRow key={entry.id} className="hover-table-row">
                      <TableCell>{entry.date}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{getUserName(entry.employeeId)}</div>
                            <div className="text-sm text-muted-foreground">{entry.position}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{getProjectName(entry.project)}</div>
                          <div className="text-sm text-muted-foreground">{entry.costCode}</div>
                        </div>
                      </TableCell>
                      <TableCell>{entry.regularHours}h</TableCell>
                      <TableCell>{entry.overtimeHours}h</TableCell>
                      <TableCell>{entry.doubleTimeHours}h</TableCell>
                      <TableCell className="font-medium">{calculateTotalHours(entry)}h</TableCell>
                      <TableCell className="font-medium">${calculateTotalPay(entry).toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(entry)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {canWrite('/timesheets') && (
                              <DropdownMenuItem onClick={() => handleEdit(entry)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Timesheet
                              </DropdownMenuItem>
                            )}
                            {canDo('approve_timesheet') && entry.status === 'submitted' && (
                              <DropdownMenuItem onClick={() => handleApprove(entry)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Review & Approve
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem>
                              <FileText className="h-4 w-4 mr-2" />
                              Generate Report
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {canDelete('/timesheets') && (
                              <DropdownMenuItem onClick={() => handleDelete(entry)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Timesheet
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payroll" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payroll Summary
              </CardTitle>
              <CardDescription>Weekly payroll calculations and summaries</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Total Hours</TableHead>
                    <TableHead>Timesheets</TableHead>
                    <TableHead>Total Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(payrollSummaries as any[]).map((summary, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{summary.employee}</TableCell>
                      <TableCell>{summary.totalHours}h</TableCell>
                      <TableCell>{summary.timesheetCount}</TableCell>
                      <TableCell className="font-bold">${(summary.totalCost ?? 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approval" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Approval Queue
              </CardTitle>
              <CardDescription>Timesheets pending approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timesheets.filter(ts => ts.status === 'submitted').map((timesheet) => (
                  <div key={timesheet.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(timesheet.status)}
                        <div>
                          <div className="font-medium">{getUserName(timesheet.employeeId)}</div>
                          <div className="text-sm text-muted-foreground">
                            {timesheet.date} • {calculateTotalHours(timesheet)} hours • ${calculateTotalPay(timesheet).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                    {canDo('approve_timesheet') && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleApprove(timesheet)}>
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(timesheet)}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Time Reports
              </CardTitle>
              <CardDescription>Generate and export timesheet reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">245.5h</div>
                    <div className="text-sm text-muted-foreground">Total Hours This Week</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">$9,847</div>
                    <div className="text-sm text-muted-foreground">Total Payroll This Week</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">12</div>
                    <div className="text-sm text-muted-foreground">Pending Approvals</div>
                  </CardContent>
                </Card>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">Export Weekly Report</Button>
                <Button variant="outline">Export Payroll Summary</Button>
                <Button variant="outline">Export Time Analysis</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock8 className="h-5 w-5" />
                Timesheet Settings
              </CardTitle>
              <CardDescription>Configure timesheet policies and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Regular Hours Per Day</Label>
                    <Input defaultValue="8" />
                  </div>
                  <div>
                    <Label>Overtime Threshold</Label>
                    <Input defaultValue="40" />
                  </div>
                  <div>
                    <Label>Double Time Threshold</Label>
                    <Input defaultValue="12" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Approval Required</Label>
                    <Select defaultValue="supervisor">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Approval</SelectItem>
                        <SelectItem value="supervisor">Supervisor Approval</SelectItem>
                        <SelectItem value="manager">Manager Approval</SelectItem>
                        <SelectItem value="both">Both Required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Submission Deadline</Label>
                    <Select defaultValue="friday">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="friday">End of Week</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto modal-content">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'add' ? 'New Timesheet Entry' : 
               dialogMode === 'edit' ? 'Edit Timesheet Entry' : 'View Timesheet Entry'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'add' ? 'Create a new timesheet entry' : 
               dialogMode === 'edit' ? 'Modify timesheet details' : 'View timesheet information'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="hours">Hours & Pay</TabsTrigger>
              <TabsTrigger value="project">Project Details</TabsTrigger>
              <TabsTrigger value="approval">Approval</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={formData.date || ''}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Employee</Label>
                  <Select
                    value={formData.employeeId || ''}
                    onValueChange={(value) => setFormData({...formData, employeeId: value, employee: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.first_name} {u.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employee ID</Label>
                  <Input 
                    value={formData.employeeId || ''}
                    onChange={(e) => setFormData({...formData, employeeId: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <Select 
                    value={formData.position || ''} 
                    onValueChange={(value) => setFormData({...formData, position: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Carpenter">Carpenter</SelectItem>
                      <SelectItem value="Electrician">Electrician</SelectItem>
                      <SelectItem value="Plumber">Plumber</SelectItem>
                      <SelectItem value="Project Manager">Project Manager</SelectItem>
                      <SelectItem value="Heavy Equipment Operator">Heavy Equipment Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="hours" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Regular Hours</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={formData.regularHours || 0}
                    onChange={(e) => setFormData({...formData, regularHours: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Hourly Rate</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.hourlyRate || 0}
                    onChange={(e) => setFormData({...formData, hourlyRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Overtime Hours</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={formData.overtimeHours || 0}
                    onChange={(e) => setFormData({...formData, overtimeHours: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Overtime Rate</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.overtimeRate || 0}
                    onChange={(e) => setFormData({...formData, overtimeRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Double Time Hours</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={formData.doubleTimeHours || 0}
                    onChange={(e) => setFormData({...formData, doubleTimeHours: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Double Time Rate</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.doubleTimeRate || 0}
                    onChange={(e) => setFormData({...formData, doubleTimeRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
              </div>
              {formData.regularHours !== undefined && formData.hourlyRate !== undefined && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm font-medium">Pay Calculation</div>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>Regular Pay: ${((formData.regularHours || 0) * (formData.hourlyRate || 0)).toFixed(2)}</div>
                    <div>Overtime Pay: ${((formData.overtimeHours || 0) * (formData.overtimeRate || 0)).toFixed(2)}</div>
                    <div>Double Time Pay: ${((formData.doubleTimeHours || 0) * (formData.doubleTimeRate || 0)).toFixed(2)}</div>
                    <div className="font-bold">Total Pay: ${(
                      (formData.regularHours || 0) * (formData.hourlyRate || 0) +
                      (formData.overtimeHours || 0) * (formData.overtimeRate || 0) +
                      (formData.doubleTimeHours || 0) * (formData.doubleTimeRate || 0)
                    ).toFixed(2)}</div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="project" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Project</Label>
                  <Select 
                    value={formData.project || ''} 
                    onValueChange={(value) => setFormData({...formData, project: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Office Building A">Office Building A</SelectItem>
                      <SelectItem value="Residential Complex B">Residential Complex B</SelectItem>
                      <SelectItem value="Warehouse C">Warehouse C</SelectItem>
                      <SelectItem value="Mall Renovation D">Mall Renovation D</SelectItem>
                      <SelectItem value="Highway Extension E">Highway Extension E</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Project Code</Label>
                  <Input 
                    value={formData.projectCode || ''}
                    onChange={(e) => setFormData({...formData, projectCode: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Cost Code</Label>
                  <Select 
                    value={formData.costCode || ''} 
                    onValueChange={(value) => setFormData({...formData, costCode: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cost code" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC-001">CC-001 - Carpentry</SelectItem>
                      <SelectItem value="CC-002">CC-002 - Electrical</SelectItem>
                      <SelectItem value="CC-003">CC-003 - Plumbing</SelectItem>
                      <SelectItem value="CC-004">CC-004 - Management</SelectItem>
                      <SelectItem value="CC-005">CC-005 - Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Work Description</Label>
                  <Textarea 
                    value={formData.description || ''}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    disabled={dialogMode === 'view'}
                    placeholder="Describe the work performed"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="approval" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={formData.status || ''} 
                    onValueChange={(value) => setFormData({...formData, status: value as any})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Submitted Date</Label>
                  <Input 
                    type="date"
                    value={formData.submittedDate || ''}
                    onChange={(e) => setFormData({...formData, submittedDate: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Approved By</Label>
                  <Input 
                    value={formData.approvedBy || ''}
                    onChange={(e) => setFormData({...formData, approvedBy: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Approved Date</Label>
                  <Input 
                    type="date"
                    value={formData.approvedDate || ''}
                    onChange={(e) => setFormData({...formData, approvedDate: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  disabled={dialogMode === 'view'}
                  placeholder="Additional notes or comments"
                  rows={6}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            {dialogMode !== 'view' && (
              <Button onClick={handleSaveTimesheet} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : dialogMode === 'add' ? 'Create Entry' : 'Save Changes'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto modal-content">
          <DialogHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Timesheet Approval Review</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Carefully review the timesheet details before making your decision
                </p>
              </div>
            </div>
          </DialogHeader>

          {selectedTimesheet && (
            <div className="space-y-6 py-4">
              <Card className="border-2 border-primary/20">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary" />
                        {getUserName(selectedTimesheet.employeeId)}
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        {getProjectName(selectedTimesheet.project)} • {selectedTimesheet.date}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        {calculateTotalHours(selectedTimesheet)}h
                      </div>
                      <div className="text-sm text-muted-foreground">Total Hours</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Regular Hours</Label>
                        <p className="font-medium mt-1">{selectedTimesheet.regularHours} hours</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Overtime Hours</Label>
                        <p className="font-medium mt-1 text-amber-600">{selectedTimesheet.overtimeHours} hours</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Double Time</Label>
                        <p className="font-medium mt-1 text-red-600">{selectedTimesheet.doubleTimeHours} hours</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Hourly Rate</Label>
                        <p className="font-mono font-medium mt-1">${selectedTimesheet.hourlyRate.toFixed(2)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Total Pay</Label>
                        <p className="font-mono text-lg font-bold mt-1 text-green-600">{formatCurrency(calculateTotalPay(selectedTimesheet), orgCurrency)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Work Description</Label>
                        <p className="text-sm mt-1">{selectedTimesheet.description}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Separator />

              {/* Decision Section */}
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium">Approval Decision</Label>
                  <div className="flex gap-3 mt-3">
                    <Button
                      variant={approvalAction === 'approve' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setApprovalAction('approve')}
                      className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Timesheet
                    </Button>
                    <Button
                      variant={approvalAction === 'reject' ? 'destructive' : 'outline'}
                      size="sm"
                      onClick={() => setApprovalAction('reject')}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject Timesheet
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="approvalNotes" className="text-base font-medium">
                    {approvalAction === 'approve' ? 'Approval Comments' : 'Rejection Reason'}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="approvalNotes"
                    placeholder={approvalAction === 'approve' 
                      ? "Add any comments for this approval..."
                      : "Please provide a reason for rejection..."}
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    rows={4}
                    className="mt-2 form-control-enhanced"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-6 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowApprovalModal(false);
                setApprovalAction(null);
                setApprovalNotes('');
              }}
            >
              Cancel Review
            </Button>
            <Button 
              onClick={() => handleApprovalSubmit(approvalAction === 'approve')}
              disabled={!approvalAction || (approvalAction === 'reject' && !approvalNotes.trim())}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {approvalAction === 'approve' ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Timesheet
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Timesheet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="modal-content">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Timesheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this timesheet? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleConfirmDelete}>
              Delete Timesheet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}